import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import {Box, CircularProgress, Stack} from "@mui/material";
import {formatDateHM, formatDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useTranslation} from "react-i18next";
import {DigitalSigningStatusView} from "../digitalSigning.model";

// A live link shows a spinner - the folio view follows it - instead of an icon
type StatusLine = {Icon?: typeof DrawOutlinedIcon, text: string};

/** The status as an icon (a spinner while the guest is still at it) plus words - never by color alone. */
const DigitalSigningStatusLine = ({signing}: {signing: DigitalSigningStatusView}) => {
    const {t} = useTranslation();

    const line = ((): StatusLine | undefined => {
        switch (signing.status) {
            case 'PENDING':
                return {text: t('Waiting for the guest')};
            case 'OPENED':
                return {text: t('Opened on a device')};
            case 'SIGNED':
                return {Icon: DrawOutlinedIcon, text: t('Signed at {{time}}', {time: formatDateHM(signing.signedAt)})};
            case 'CONFIRMED':
                return {
                    Icon: CheckCircleOutlineIcon,
                    text: t('Confirmed on {{date}}', {date: formatDateTime('dd.MM.yyyy HH:mm', signing.confirmedAt)})
                };
            default:
                return undefined;
        }
    })();
    if (!line) return null;
    const {Icon, text} = line;

    return (
        <Stack direction={'row'} gap={1} alignItems={'center'} role={'status'}>
            {Icon
                ? <Icon fontSize={'small'} color={'primary'}/>
                // In the 20px box of the icons, so the words don't move when it turns into one
                : (
                    <Box sx={{width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0}}>
                        <CircularProgress size={16} thickness={5} aria-hidden/>
                    </Box>
                )}
            <Box component={'span'} sx={{fontSize: 13, lineHeight: '18px', fontWeight: 500}}>{text}</Box>
        </Stack>
    );
};

export default DigitalSigningStatusLine;
